import { styled } from '@mui/material';
import { HelixPalette, HelixStyles } from '../../helix';
/**
 * Base style for a button.
 */
export const ButtonBase = {
    alignItems: 'center',
    border: 'none',
    display: 'flex',
    justifyContent: 'center',
    ':focus': { outline: 'none' },
};
/**
 * Blue border used with :focus-visible.
 */
export const ButtonBaseFocus = {
    outline: HelixStyles.focusVisible4.outline,
    outlineOffset: HelixStyles.focusVisible4.outlineOffset,
    borderRadius: '4px',
};
/**
 * Cursor style used with :hover.
 */
export const ButtonBaseHover = {
    cursor: 'pointer',
};
/**
 * Base style for a disabled button.
 */
export const ButtonBaseDisabled = {
    backgroundColor: HelixPalette.neutral10,
    cursor: 'default',
    color: HelixPalette.neutral50,
    svg: { fill: HelixPalette.neutral50 },
};
/**
 * The primary themed contained button.
 */
export const PrimaryButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: HelixPalette.teal80, color: HelixPalette.white, svg: { fill: HelixPalette.white }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { backgroundColor: HelixPalette.teal70 }), ':active:focus': {
        backgroundColor: HelixPalette.teal90,
    }, ':focus-visible': Object.assign({}, ButtonBaseFocus), ':disabled': Object.assign({}, ButtonBaseDisabled) }));
/**
 * The secondary themed contained button.
 */
export const SecondaryButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: HelixPalette.neutral05, color: HelixPalette.neutral100, svg: { fill: HelixPalette.neutral100 }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { backgroundColor: HelixPalette.neutral10 }), ':active:focus': {
        backgroundColor: HelixPalette.neutral20,
    }, ':focus-visible': Object.assign({}, ButtonBaseFocus), ':disabled': Object.assign({}, ButtonBaseDisabled) }));
/**
 * The danger themed contained button.
 */
export const DangerButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: HelixPalette.red50, color: HelixPalette.white, svg: { fill: HelixPalette.white }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { backgroundColor: HelixPalette.red40 }), ':active:focus': {
        backgroundColor: HelixPalette.red60,
    }, ':focus-visible': Object.assign({}, ButtonBaseFocus), ':disabled': Object.assign({}, ButtonBaseDisabled) }));
/**
 * The teal themed universal button.
 */
export const TealUniversalButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: 'transparent', color: HelixPalette.teal80, svg: { fill: HelixPalette.teal80 }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { color: HelixPalette.teal70, svg: { fill: HelixPalette.teal70 } }), ':active:focus': {
        color: HelixPalette.teal90,
        svg: { fill: HelixPalette.teal90 },
    }, ':focus-visible': Object.assign({}, ButtonBaseFocus), ':disabled': Object.assign(Object.assign({}, ButtonBaseDisabled), { backgroundColor: 'transparent' }) }));
/**
 * The grey themed universal button.
 */
export const GreyUniversalButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: 'transparent', color: HelixPalette.neutral90, svg: { fill: HelixPalette.neutral90 }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { color: HelixPalette.neutral80, svg: { fill: HelixPalette.neutral80 } }), ':active:focus': {
        color: HelixPalette.neutral100,
        svg: { fill: HelixPalette.neutral100 },
    }, ':focus-visible': Object.assign({}, ButtonBaseFocus), ':disabled': Object.assign(Object.assign({}, ButtonBaseDisabled), { backgroundColor: 'transparent' }) }));
/**
 * The white themed universal button.
 */
export const WhiteUniversalButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: 'transparent', color: HelixPalette.white, svg: { fill: HelixPalette.white }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { color: HelixPalette.neutral10, svg: { fill: HelixPalette.neutral10 } }), ':active:focus': {
        color: HelixPalette.neutral20,
        svg: { fill: HelixPalette.neutral20 },
    }, ':focus-visible': Object.assign({}, ButtonBaseFocus), ':disabled': Object.assign(Object.assign({}, ButtonBaseDisabled), { backgroundColor: 'transparent' }) }));
/**
 * The danger themed universal button.
 */
export const DangerUniversalButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: 'transparent', color: HelixPalette.red50, svg: { fill: HelixPalette.red50 }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { color: HelixPalette.red40, svg: { fill: HelixPalette.red40 } }), ':active:focus': {
        color: HelixPalette.red60,
        svg: { fill: HelixPalette.red60 },
    }, ':focus-visible': Object.assign({}, ButtonBaseFocus), ':disabled': Object.assign(Object.assign({}, ButtonBaseDisabled), { backgroundColor: 'transparent' }) }));
/**
 * The black themed ghost button.
 */
export const BlackGhostButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: 'transparent', border: `solid 2px ${HelixPalette.neutral90}`, color: HelixPalette.neutral90, svg: { fill: HelixPalette.neutral90 }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { borderColor: HelixPalette.neutral80, color: HelixPalette.neutral80, svg: { fill: HelixPalette.neutral80 } }), ':active:focus': {
        borderColor: HelixPalette.neutral100,
        color: HelixPalette.neutral100,
        svg: { fill: HelixPalette.neutral100 },
    }, ':focus-visible': {
        backgroundColor: HelixPalette.blue70,
        color: HelixPalette.white,
        svg: { fill: HelixPalette.white },
    }, ':disabled': Object.assign(Object.assign({}, ButtonBaseDisabled), { backgroundColor: 'transparent', borderColor: HelixPalette.neutral50 }) }));
/**
 * The white themed ghost button.
 */
export const WhiteGhostButton = styled('button')(Object.assign(Object.assign({}, ButtonBase), { backgroundColor: 'transparent', border: `solid 2px ${HelixPalette.white}`, color: HelixPalette.white, svg: { fill: HelixPalette.white }, ':hover': Object.assign(Object.assign({}, ButtonBaseHover), { borderColor: HelixPalette.neutral10, color: HelixPalette.neutral10, svg: { fill: HelixPalette.neutral10 } }), ':active:focus': {
        borderColor: HelixPalette.neutral20,
        color: HelixPalette.neutral20,
        svg: { fill: HelixPalette.neutral20 },
    }, ':focus-visible': {
        backgroundColor: HelixPalette.blue70,
    }, ':disabled': Object.assign(Object.assign({}, ButtonBaseDisabled), { backgroundColor: 'transparent', borderColor: HelixPalette.neutral50 }) }));
export const contentStyles = {
    contained: {
        large: {
            iconSize: { height: '1.5rem', width: '1.5rem' },
            margin: '0.75rem',
            fontStyle: Object.assign(Object.assign({}, HelixStyles.style_08), { color: 'undefined' }),
            loaderSize: 24,
        },
        medium: {
            iconSize: { height: '1.25rem', width: '1.25rem' },
            margin: '0.5rem',
            fontStyle: Object.assign(Object.assign({}, HelixStyles.style_09), { color: 'undefined' }),
            loaderSize: 20,
        },
        small: {
            iconSize: { height: '1rem', width: '1rem' },
            margin: '0.5rem',
            fontStyle: Object.assign(Object.assign({}, HelixStyles.style_10), { color: 'undefined' }),
            loaderSize: 16,
        },
    },
    ghost: {
        iconSize: { height: '1rem', width: '1rem' },
        margin: '0.5rem',
        fontStyle: Object.assign(Object.assign({}, HelixStyles.style_10), { color: 'undefined' }),
        loaderSize: 16,
    },
    universal: {
        large: {
            iconSize: { height: '1.5rem', width: '1.5rem' },
            margin: '0.5rem',
            fontStyle: Object.assign(Object.assign({}, HelixStyles.style_08), { color: 'undefined' }),
            loaderSize: 24,
        },
        medium: {
            iconSize: { height: '1.25rem', width: '1.25rem' },
            margin: '0.375rem',
            fontStyle: Object.assign(Object.assign({}, HelixStyles.style_09), { color: 'undefined' }),
            loaderSize: 20,
        },
        small: {
            iconSize: { height: '1rem', width: '1rem' },
            margin: '0.375rem',
            fontStyle: Object.assign(Object.assign({}, HelixStyles.style_10), { color: 'undefined' }),
            loaderSize: 16,
        },
    },
};
